"use client"

import { useEffect, useState } from "react"
import { ShieldCheck, BarChart3, Check, RotateCcw } from "lucide-react"
import { getConsent, setConsent, onConsentChange, type ConsentState } from "@/lib/analytics"

/**
 * User-facing cookie preferences control — Aperture-Light skin.
 *
 * Lives on /privacy (anchored as #cookies) and is linked from the
 * cookie consent banner's "settings" text. Lets the visitor flip
 * between "Accept analytics" and "Essential only" at any time after
 * the initial banner has been dismissed.
 *
 * The heavy lifting (localStorage persistence + cross-tab dispatch)
 * is already handled in lib/analytics; this is just the UI.
 *
 * Why it lives here instead of /settings: the /settings page is
 * account-scoped and requires auth. Consent choices are browser-scoped
 * and apply to anonymous visitors too, so they belong on the same page
 * as the privacy policy itself.
 */

export function CookieSettings() {
  // "pending" = the banner hasn't been shown/resolved yet (fresh visit
  // or localStorage cleared). We still render the UI with the essential-
  // only preset highlighted, so the user can lock a choice without
  // waiting for the banner to re-trigger.
  const [consent, setConsentState] = useState<ConsentState>("pending")
  const [mounted, setMounted] = useState(false)
  const [flashSaved, setFlashSaved] = useState(false)

  useEffect(() => {
    setMounted(true)
    setConsentState(getConsent())
    const off = onConsentChange((next) => setConsentState(next))
    return off
  }, [])

  if (!mounted) {
    // Avoid an SSR/CSR mismatch on the current state — render a quiet
    // placeholder that matches the final height so nothing jumps when
    // the real UI appears.
    return (
      <div
        className="mt-4 h-[182px] rounded-2xl"
        style={{
          background: "#FBF8F0",
          border: "1px solid rgba(10,10,10,0.08)",
        }}
      />
    )
  }

  const apply = (next: "accepted" | "rejected") => {
    setConsent(next)
    setFlashSaved(true)
    setTimeout(() => setFlashSaved(false), 2400)
  }

  const reset = () => {
    if (typeof window === "undefined") return
    window.localStorage.removeItem("db_cookie_consent")
    setConsentState("pending")
    setFlashSaved(true)
    setTimeout(() => setFlashSaved(false), 2400)
  }

  const acceptedActive = consent === "accepted"
  const rejectedActive = consent === "rejected"

  const FM = "'JetBrains Mono', ui-monospace, monospace"
  const ACC = "#0F5132"
  const HAIR = "rgba(10,10,10,0.08)"
  const HAIR_STRONG = "rgba(10,10,10,0.12)"

  return (
    <div
      id="cookies-control"
      className="mt-4 rounded-2xl overflow-hidden"
      style={{
        background: "#FAF7EE",
        border: `1px solid ${HAIR}`,
        boxShadow: "0 1px 0 rgba(255,255,255,0.6) inset",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between gap-3 px-5 py-3.5 border-b"
        style={{ borderColor: HAIR }}
      >
        <div className="flex items-center gap-2">
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: ACC }}
          />
          <p
            style={{
              fontFamily: FM,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "rgba(10,10,10,0.55)",
            }}
          >
            Your cookie preferences
          </p>
        </div>
        <span
          className={`transition-opacity duration-200 ${
            flashSaved ? "opacity-100" : "opacity-0"
          }`}
          style={{ fontSize: 11, fontWeight: 600, color: ACC }}
          aria-live="polite"
        >
          ✓ Saved
        </span>
      </div>

      {/* Current state summary */}
      <div
        className="px-5 py-3.5 border-b"
        style={{ borderColor: HAIR }}
      >
        <p
          className="leading-relaxed"
          style={{ fontSize: 12, color: "rgba(10,10,10,0.62)" }}
        >
          {consent === "accepted" && (
            <>
              <span style={{ color: ACC, fontWeight: 700 }}>
                Analytics cookies allowed.
              </span>{" "}
              We&rsquo;re collecting anonymous usage data to improve the
              product.
            </>
          )}
          {consent === "rejected" && (
            <>
              <span style={{ color: "#0A0A0A", fontWeight: 700 }}>
                Essential cookies only.
              </span>{" "}
              No analytics, no tracking. Only the cookies required for
              sign-in, uploads, and preferences.
            </>
          )}
          {consent === "pending" && (
            <>
              <span style={{ color: "#B85C00", fontWeight: 700 }}>
                No choice recorded yet.
              </span>{" "}
              Pick one below to lock it in, or the banner will ask you
              again on next visit.
            </>
          )}
        </p>
      </div>

      {/* Toggle cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3">
        <button
          type="button"
          onClick={() => apply("rejected")}
          aria-pressed={rejectedActive}
          className="text-left rounded-xl px-3.5 py-3 transition-all duration-150"
          style={{
            background: rejectedActive ? "rgba(15,81,50,0.06)" : "#FBF8F0",
            border: `1px solid ${
              rejectedActive ? "rgba(15,81,50,0.30)" : HAIR_STRONG
            }`,
          }}
        >
          <div className="flex items-center gap-2 mb-1.5">
            <ShieldCheck
              size={12}
              strokeWidth={2.25}
              style={{ color: rejectedActive ? ACC : "rgba(10,10,10,0.55)" }}
            />
            <span
              style={{
                fontFamily: FM,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: rejectedActive ? ACC : "rgba(10,10,10,0.62)",
              }}
            >
              Essential only
            </span>
            {rejectedActive && (
              <Check
                size={11}
                className="ml-auto"
                strokeWidth={3}
                style={{ color: ACC }}
              />
            )}
          </div>
          <p
            className="leading-snug"
            style={{ fontSize: 11.5, color: "rgba(10,10,10,0.55)" }}
          >
            Sign-in, uploads, preferences. Zero tracking.
          </p>
        </button>

        <button
          type="button"
          onClick={() => apply("accepted")}
          aria-pressed={acceptedActive}
          className="text-left rounded-xl px-3.5 py-3 transition-all duration-150"
          style={{
            background: acceptedActive ? "rgba(15,81,50,0.06)" : "#FBF8F0",
            border: `1px solid ${
              acceptedActive ? "rgba(15,81,50,0.30)" : HAIR_STRONG
            }`,
          }}
        >
          <div className="flex items-center gap-2 mb-1.5">
            <BarChart3
              size={12}
              strokeWidth={2.25}
              style={{ color: acceptedActive ? ACC : "rgba(10,10,10,0.55)" }}
            />
            <span
              style={{
                fontFamily: FM,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: acceptedActive ? ACC : "rgba(10,10,10,0.62)",
              }}
            >
              Accept analytics
            </span>
            {acceptedActive && (
              <Check
                size={11}
                className="ml-auto"
                strokeWidth={3}
                style={{ color: ACC }}
              />
            )}
          </div>
          <p
            className="leading-snug"
            style={{ fontSize: 11.5, color: "rgba(10,10,10,0.55)" }}
          >
            Anonymous usage + masked session replays to improve UX.
          </p>
        </button>
      </div>

      {/* Footer — reset + reload hint */}
      <div
        className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-t"
        style={{ borderColor: HAIR }}
      >
        <p
          className="leading-snug"
          style={{ fontSize: 10.5, color: "rgba(10,10,10,0.42)" }}
        >
          Changes save instantly. For analytics to fully unload after
          revoking, reload the page.
        </p>
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md transition"
          style={{
            fontSize: 10.5,
            fontWeight: 500,
            color: "rgba(10,10,10,0.55)",
            background: "transparent",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = ACC
            e.currentTarget.style.background = "rgba(15,81,50,0.06)"
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "rgba(10,10,10,0.55)"
            e.currentTarget.style.background = "transparent"
          }}
        >
          <RotateCcw size={10} />
          Reset (show banner again)
        </button>
      </div>
    </div>
  )
}
